import { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { BRAND } from "../lib/brand";
import { CTA, GlassCard, PageHeader, Shell } from "../components/site/Primitives";

const FIELDS = [
  ["name", "Full name", "text", true],
  ["email", "Email", "email", true],
  ["company", "Company", "text", false],
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email address.";
    if (form.message.trim().length < 5) next.message = "Tell us briefly what you need.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const { data } = await api.post("/public/contact", form);
      setStatus("success");
      toast.success(data.message);
    } catch (err) {
      setStatus("error");
      toast.error(apiError(err));
    }
  };

  if (status === "success")
    return (
      <div data-testid="contact-page">
        <PageHeader testId="contact-title" eyebrow="Contact" title="Message received." />
        <section className="py-20">
          <Shell>
            <GlassCard testId="contact-success" hover={false} className="max-w-xl p-8">
              <Icons.CheckCircle2 className="h-6 w-6 text-elv-green" />
              <p className="mt-5 text-lg text-elv-text">Thanks — we have your message.</p>
              <p className="mt-3 text-sm text-elv-dim">
                We reply to enquiries by email at {BRAND.contactEmail}. If it's urgent, book a demo slot and
                we'll cover it live.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <CTA to="/book-demo" testId="contact-success-demo">
                  Book a Free AI Demo
                </CTA>
                <CTA to="/" variant="ghost" testId="contact-success-home">
                  Back to home
                </CTA>
              </div>
            </GlassCard>
          </Shell>
        </section>
      </div>
    );

  return (
    <div data-testid="contact-page">
      <PageHeader
        testId="contact-title"
        eyebrow="Contact"
        title="Tell us what you want to automate."
        sub="Email works, or send the form below. We don't publish a phone number or office address — everything runs through email and scheduled calls."
      />
      <section className="py-20">
        <Shell>
          <div className="grid gap-8 lg:grid-cols-[1fr_0.8fr]">
            <GlassCard testId="contact-form-card" hover={false} className="p-6 sm:p-8">
              <form onSubmit={submit} noValidate className="grid gap-5">
                {FIELDS.map(([key, label, type, required]) => (
                  <Field
                    key={key}
                    id={key}
                    label={label}
                    type={type}
                    required={required}
                    value={form[key]}
                    onChange={set(key)}
                    error={errors[key]}
                  />
                ))}
                <div>
                  <label htmlFor="message" className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">
                    How can we help? *
                  </label>
                  <textarea
                    id="message"
                    data-testid="contact-input-message"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className="mt-2.5 w-full rounded-md border border-elv-line bg-white/[0.02] px-3.5 py-3 text-sm text-elv-text outline-none transition-colors duration-300 focus:border-elv-green/50"
                  />
                  {errors.message ? (
                    <p data-testid="contact-error-message" className="mt-2 text-xs text-red-300">
                      {errors.message}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center gap-2 rounded-md bg-elv-green px-6 py-3 text-sm font-semibold text-[#04120A] transition-colors duration-300 hover:bg-[#5cffa0] disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <Icons.Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Icons.Send className="h-4 w-4" />
                    )}
                    {status === "loading" ? "Sending…" : "Send message"}
                  </button>
                  {status === "error" ? (
                    <button
                      type="submit"
                      data-testid="contact-retry"
                      className="text-xs text-elv-dim underline underline-offset-4 hover:text-elv-text"
                    >
                      Retry
                    </button>
                  ) : null}
                </div>
              </form>
            </GlassCard>

            <div className="grid gap-3">
              <GlassCard testId="contact-email-card" className="p-6">
                <Icons.Mail className="h-4 w-4 text-elv-green" />
                <p className="mt-4 text-sm font-medium text-elv-text">Email</p>
                <a
                  href={`mailto:${BRAND.contactEmail}`}
                  data-testid="contact-email-link"
                  className="mt-2 block font-mono text-sm text-elv-green hover:text-elv-cyan"
                >
                  {BRAND.contactEmail}
                </a>
              </GlassCard>
              <GlassCard testId="contact-demo-card" className="p-6">
                <Icons.CalendarCheck className="h-4 w-4 text-elv-cyan" />
                <p className="mt-4 text-sm font-medium text-elv-text">Prefer a live walkthrough?</p>
                <p className="mt-2 text-xs text-elv-dim">
                  Book a demo and we'll walk the platform against your workflow.
                </p>
                <div className="mt-5">
                  <CTA to="/book-demo" variant="ghost" testId="contact-demo-cta">
                    Book a Free AI Demo
                  </CTA>
                </div>
              </GlassCard>
              <GlassCard testId="contact-social-card" className="p-6">
                <Icons.Share2 className="h-4 w-4 text-elv-dim" />
                <p className="mt-4 text-sm font-medium text-elv-text">Social</p>
                <ul className="mt-3 space-y-2">
                  {BRAND.social.map((s) => (
                    <li key={s.label} className="text-xs text-elv-dim">
                      {s.label} — {s.note}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            </div>
          </div>
        </Shell>
      </section>
    </div>
  );
}

export const Field = ({ id, label, type = "text", required, value, onChange, error, placeholder }) => (
  <div>
    <label htmlFor={id} className="text-[11px] uppercase tracking-[0.2em] text-elv-dim">
      {label} {required ? "*" : ""}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      data-testid={`contact-input-${id}`}
      className="mt-2.5 w-full rounded-md border border-elv-line bg-white/[0.02] px-3.5 py-3 text-sm text-elv-text outline-none transition-colors duration-300 focus:border-elv-green/50"
    />
    {error ? (
      <p data-testid={`contact-error-${id}`} className="mt-2 text-xs text-red-300">
        {error}
      </p>
    ) : null}
  </div>
);
